import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PeopleChecklist } from "@/components/people/PeopleChecklist";
import { Button, Select, TextButton } from "@/components/ui";
import { useCreateOrder, useMenuboards, usePeople } from "@/lib/api/queries";
import type { OrderSummary } from "@/lib/api/types";
import { copyText } from "@/lib/clipboard";

export function CreateOrderForm({
  workspaceId,
  onCreated,
}: {
  workspaceId: string;
  onCreated?: (order: OrderSummary) => void;
}) {
  const navigate = useNavigate();
  const { data: menuboards } = useMenuboards(workspaceId);
  const { data: people } = usePeople(workspaceId);
  const createOrder = useCreateOrder(workspaceId);
  const [menuboardId, setMenuboardId] = useState("");
  const [personIds, setPersonIds] = useState<string[]>([]);

  function handleCreate() {
    if (!menuboardId || personIds.length === 0) return;
    createOrder.mutate(
      { menuboard_id: menuboardId, person_ids: personIds },
      {
        onSuccess: async (order) => {
          const ok = await copyText(`${location.origin}/o/${order.slug}`);
          if (ok) toast.success("링크를 복사했어요");
          else toast.error("복사하지 못했어요. 링크를 직접 공유해 주세요.");
          setPersonIds([]);
          onCreated?.(order);
        },
        onError: () => toast.error("만들지 못했어요. 다시 시도해 주세요."),
      },
    );
  }

  if (!menuboards || menuboards.length === 0) {
    return (
      <div className="rounded-md border border-line-normal p-4">
        <p className="text-body-2 text-label-alternative">
          먼저 메뉴판을 만들어 주세요. 메뉴판 사진을 올리면 메뉴를 읽어와요.
        </p>
        <div className="mt-2">
          <TextButton chevron onClick={() => navigate("/menuboards")}>
            메뉴판 만들러 가기
          </TextButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Select
        label="메뉴판"
        value={menuboardId}
        onChange={(e) => setMenuboardId(e.target.value)}
        options={menuboards.map((b) => ({ value: b.id, label: b.name }))}
      />

      <PeopleChecklist people={people} selectedIds={personIds} onChange={setPersonIds} />

      <Button
        fullWidth
        onClick={handleCreate}
        disabled={!menuboardId || personIds.length === 0 || createOrder.isPending}
      >
        링크 만들기
      </Button>
    </div>
  );
}
